import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { AVAILABLE_ACTUATION_KINDS } from '../../constants/actuation-kinds';
import { buildSearchItems } from '../../services/studies/utils/build-search-items';
import { Document } from '../../types';
import { documentStructureToHtml } from '../../services/studies/utils/document-structure-to-html/document-structure-to-html';
import { StudiesService } from '../../services/studies/studies.service';
import { mapActuationsToDocuments } from './utils/map-actuations-to-documents';

@Component({
  selector: 'app-study-documents-explorer',
  template: `
    <app-base-study-documents-explorer
      [documents]="documents"
      [(selectedDocument)]="selectedDocument"
      [search]="search"
      [loading]="loading"
    ></app-base-study-documents-explorer>
  `,
})
export class StudyDocumentsExplorerContainerComponent implements OnChanges {
  @Input() studyId: number;
  @Input() search: string;
  @Input() selectedDocumentId: string;

  documents: Document[];
  loadingActuations = false;
  loadingDocumentContentValue = false;

  private _selectedDocument: Document;

  get loading(): boolean {
    return this.loadingActuations || this.loadingDocumentContentValue;
  }

  get selectedDocument(): Document {
    return this._selectedDocument;
  }

  set selectedDocument(value: Document) {
    if (!value) {
      return;
    }
    if (!value.content.value) {
      this.updateDocumentContentValue(value.id);
    } else {
      this._selectedDocument = value;
    }
  }

  constructor(private studiesService: StudiesService) {}

  async ngOnChanges(changes: SimpleChanges) {
    const { studyIdHasChanged, selectedDocumentIdHasChanged } =
      this.getChanges(changes);

    if (selectedDocumentIdHasChanged) {
      this.updateSelectedDocument();
    }

    if (studyIdHasChanged) {
      this.resetState();

      if (!this.studyId) {
        return;
      }

      await this.updateDocuments();
      this.updateSelectedDocument();

      return;
    }
  }

  private resetState() {
    this.documents = null;
    this.selectedDocument = null;
  }

  private getChanges(changes: SimpleChanges) {
    return {
      studyIdHasChanged:
        changes['studyId']?.previousValue !== changes['studyId']?.currentValue,
      searchHasChanged:
        changes['search']?.previousValue !== changes['search']?.currentValue,
      selectedDocumentIdHasChanged:
        changes['selectedDocumentId']?.previousValue !==
        changes['selectedDocumentId']?.currentValue,
    };
  }

  private async updateDocuments() {
    this.loadingActuations = true;
    const { data: study, error } = await this.studiesService.getStudy(
      this.studyId,
    );

    this.loadingActuations = false;

    if (error) {
      /**
       * TODO: Properly handle the case where documents are not found
       * or an error occurs.
       */
      return;
    }
    const actuations = study.actuations.filter((actuation) =>
      AVAILABLE_ACTUATION_KINDS.includes(actuation.kind),
    );
    this.documents = mapActuationsToDocuments(actuations);
  }

  private async updateSelectedDocument() {
    if (!this.documents?.length) {
      return;
    }

    const lastDocument = this.documents[0];

    if (!this.selectedDocumentId) {
      this._selectedDocument = lastDocument;
    }

    this._selectedDocument =
      this.documents.find(
        (document) => document.id === this.selectedDocumentId,
      ) || lastDocument;

    if (!this._selectedDocument.content.value) {
      await this.updateDocumentContentValue(this._selectedDocument.id);
    }
  }

  private async updateDocumentContentValue(id: string) {
    this.loadingDocumentContentValue = true;

    const documentIndex = this.documents.findIndex((doc) => doc.id === id);
    const document = this.documents[documentIndex];
    const { data } = await this.studiesService.getStudyActuationByCve(id);

    if (document) {
      this.documents[documentIndex] = {
        ...document,
        content: {
          searchItems: buildSearchItems(data.study),
          type: 'html',
          value: documentStructureToHtml(data.documentStructure),
        },
      };
      this._selectedDocument = this.documents[documentIndex];
    }
    this.loadingDocumentContentValue = false;
  }
}
